import { useEffect, useMemo, useRef, useState } from 'react'
import { usePersonaDetail, useSaveDraft, usePublishPersona, useEffectiveAccess, useValidatorRules, useVersions } from '@/api/queries'
import type { PersonaFrontMatter, ValidationIssue } from '@/validator/validatePersona'
import { validatePersona } from '@/validator/validatePersona'
import { Badge } from '@/components/ui/Badge'

interface Props { role: string; onClose: () => void }

const BLANK_FM: PersonaFrontMatter = {
  schema_version: 1, role: '', prefix: '[]', description: '',
  tools: [], events: [], allowed_addressees: [], allowed_addressers: [], output_rules: [],
}

export function PersonaEditor({ role, onClose }: Props) {
  const detail = usePersonaDetail(role)
  const access = useEffectiveAccess()
  const rules = useValidatorRules()
  const saveDraft = useSaveDraft(role)
  const publish = usePublishPersona(role)
  const versions = useVersions(role)
  // Spec 350 — the live published version + whether it draws on a shared prompt fragment.
  const currentVersion = versions.data?.find((v) => v.current && v.kind === 'published')

  const original = useRef<{ fm: PersonaFrontMatter; body: string; etag?: string } | null>(null)
  const [fm, setFm] = useState<PersonaFrontMatter>(BLANK_FM)
  const [body, setBody] = useState('')
  const [justify, setJustify] = useState('')
  const [showPublish, setShowPublish] = useState(false)
  const [confirmRole, setConfirmRole] = useState('')

  useEffect(() => {
    if (detail.data) {
      original.current = { fm: detail.data.data.frontMatter, body: detail.data.data.body, etag: detail.data.etag }
      setFm(detail.data.data.frontMatter)
      setBody(detail.data.data.body)
    }
  }, [detail.data])

  // Keep prefix locked to role.
  useEffect(() => { setFm((x) => ({ ...x, prefix: `[${x.role}]` })) }, [fm.role])

  const issues: ValidationIssue[] = useMemo(() => {
    if (!rules.data) return []
    return validatePersona(fm, body, rules.data, {
      catalogIds: new Set(),
      toolClasses: new Map(),
      liveRoles: new Set(),
      configApproverKeys: new Set(),
      tokenCap: 8000,
    })
  }, [fm, body, rules.data])

  const errs = issues.filter((i) => i.severity === 'error')
  const warns = issues.filter((i) => i.severity === 'warn')
  const canPublish = (access.data?.capabilities ?? []).includes('persona.publish')

  if (detail.isLoading) return <div className="page"><p>Loading…</p></div>
  if (detail.isError) return <div className="page"><p style={{ color: 'var(--err)' }}>Failed to load {role}.</p></div>

  const handleSave = () => {
    saveDraft.mutate({ frontMatter: fm, body, etag: original.current?.etag })
  }
  const handlePublishStart = () => { setShowPublish(true); setConfirmRole(''); setJustify('') }
  const handlePublishConfirm = () => {
    publish.mutate(
      { justification: justify, etag: original.current?.etag },
      { onSuccess: () => { setShowPublish(false); onClose() } },
    )
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="mono">{fm.role || 'New_Persona'}</h1>
          <p>{errs.length} errors · {warns.length} warnings</p>
        </div>
        <div className="row">
          <button className="btn" onClick={onClose}>Back</button>
          <button className="btn" onClick={handleSave} disabled={saveDraft.isPending}>Save draft</button>
          <button
            className="btn primary"
            onClick={handlePublishStart}
            disabled={errs.length > 0 || !canPublish}
            title={!canPublish ? 'Your role lacks persona.publish — ask an administrator for Persona Approver or above.' : ''}
          >Publish…</button>
        </div>
      </div>

      {currentVersion?.usesFragments && currentVersion.usesFragments.length > 0 && (
        <div className="row" style={{ gap: 'var(--sp-8)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--sp-16)' }}>
          <strong>Effective prompt</strong>
          <span className="mono" title="hash of the expanded served prompt (matches the bridge)">
            {currentVersion.effectiveSha ?? 'n/a'}
          </span>
          <span>| published sha <span className="mono">{currentVersion.sha}</span></span>
          <span>| fragments: <span className="mono">{currentVersion.usesFragments.join(', ')}</span></span>
          {currentVersion.fragmentDriftSince
            ? <Badge tone="warn">prompt changed by a fragment edit since publish ({new Date(currentVersion.fragmentDriftSince).toLocaleString()})</Badge>
            : <Badge tone="info">fragment unchanged since publish</Badge>}
        </div>
      )}

      <div className="editor">
        <section className="field"><label>role</label>
          <input className="input mono" value={fm.role} onChange={(e) => setFm({ ...fm, role: e.target.value })} />
        </section>
        <section className="field"><label>prefix <small>auto-derived</small></label>
          <input className="input mono" value={fm.prefix} readOnly />
        </section>
        <section className="field"><label>description</label>
          <input className="input" value={fm.description} onChange={(e) => setFm({ ...fm, description: e.target.value })} />
        </section>
        <section className="field"><label>body <small>system prompt + domain notes</small></label>
          <textarea
            className="input"
            style={{ minHeight: 320, fontFamily: 'var(--font-mono)' }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            spellCheck="false"
          />
        </section>
      </div>

      {issues.length > 0 && (
        <div className="val-footer">
          {issues.map((i, idx) => (
            <span key={idx} className={`val-item ${i.severity}`}>
              <span className="mono">{i.code}</span> · {i.field} · {i.message}
            </span>
          ))}
        </div>
      )}

      {showPublish && (
        <div className="modal-scrim" onClick={() => setShowPublish(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Publish {fm.role}</h2>
            <p>Type the role name to confirm.</p>
            <input
              className="input mono"
              value={confirmRole}
              onChange={(e) => setConfirmRole(e.target.value)}
              placeholder={fm.role}
              autoFocus
            />
            <p style={{ marginTop: 'var(--sp-16)' }}>Justification (recorded in the audit log)</p>
            <textarea
              className="input"
              style={{ minHeight: 80 }}
              value={justify}
              onChange={(e) => setJustify(e.target.value)}
              placeholder="e.g. FUS-412 — tune the brand voice for premium-tier replies."
            />
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--sp-16)' }}>
              <button className="btn" onClick={() => setShowPublish(false)}>Cancel</button>
              <button
                className="btn primary"
                onClick={handlePublishConfirm}
                disabled={confirmRole !== fm.role || !justify.trim() || publish.isPending}
              >Publish {fm.role}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
